import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, User, Mail, FileText,
  CheckCircle, Loader2, UserPlus, HelpCircle, MessageSquare,
  Briefcase, MapPin, Phone, PenTool, Shirt, Home, Wrench, Gamepad2
} from 'lucide-react';
import { toast } from 'sonner';
import RetroSelect from '../RetroSelect';

const SubmitSection = () => {
  const [activeTab, setActiveTab] = useState('apply');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedType, setSubmittedType] = useState('');

  // Apply Form State
  const [applyData, setApplyData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    occupation: '',
    reason: '',
    selectedQuests: [],
  });

  const sideQuests = [
    { id: 'handwritten', label: 'Handwritten Notes & Problem Solving', icon: PenTool, color: 'neon-pink' },
    { id: 'laundry', label: 'Folding Clothes', icon: Shirt, color: 'trophy-gold' },
    { id: 'homeops', label: 'Household Tasks', icon: Home, color: 'success-green' },
    { id: 'tools', label: 'Tool Usage', icon: Wrench, color: 'warning-orange' },
  ];

  const handleQuestToggle = (questId) => {
    setApplyData(prev => ({
      ...prev,
      selectedQuests: prev.selectedQuests.includes(questId)
        ? prev.selectedQuests.filter(id => id !== questId)
        : [...prev.selectedQuests, questId]
    }));
  };

  // Inquiry Form State
  const [inquiryData, setInquiryData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const occupationOptions = [
    'Student',
    'Freelancer',
    'Employed',
    'Self-Employed',
    'Unemployed',
    'Other',
  ];

  const subjectOptions = [
    'General Question',
    'Technical Issue',
    'Payment Inquiry',
    'Partnership Opportunity',
    'Other',
  ];

  const tabs = [
    { id: 'apply', label: 'APPLY', icon: UserPlus },
    { id: 'inquiry', label: 'INQUIRY', icon: HelpCircle },
  ];

  const handleApply = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setSubmitted(true);
    setSubmittedType('apply');
    toast.success('Application submitted! We\'ll review and get back to you soon.', { duration: 5000 });
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setSubmitted(true);
    setSubmittedType('inquiry');
    toast.success('Inquiry sent! We\'ll respond to your message soon.', { duration: 5000 });
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmittedType('');
    setApplyData({ fullName: '', email: '', phone: '', location: '', occupation: '', reason: '', selectedQuests: [] });
    setInquiryData({ name: '', email: '', subject: '', message: '' });
  };

  const successMessages = {
    apply: { title: 'APPLICATION RECEIVED!', message: 'Thanks for applying! We\'ll review your application and contact you within 3-5 business days.' },
    inquiry: { title: 'MESSAGE SENT!', message: 'We\'ve received your inquiry. Our team will respond to your message as soon as possible.' },
  };

  if (submitted) {
    return (
      <section id="submit" className="py-20 px-4 sm:px-6 lg:px-8 bg-deep-purple/50">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="arcade-panel rounded-lg p-8 sm:p-12 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 bg-success-green rounded-full flex items-center justify-center"
            >
              <CheckCircle className="w-10 h-10 text-dark-border" />
            </motion.div>

            <h3 className="font-pixel text-lg sm:text-xl text-success-green mb-4">
              {successMessages[submittedType]?.title}
            </h3>
            <p className="font-terminal text-xl text-lavender mb-6">
              {successMessages[submittedType]?.message}
            </p>

            <button
              onClick={resetForm}
              className="btn-arcade font-pixel text-xs px-6 py-3 bg-neon-pink text-dark-border"
            >
              SUBMIT ANOTHER
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="submit" className="py-20 px-4 sm:px-6 lg:px-8 bg-deep-purple/50">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <MessageSquare className="w-6 h-6 text-neon-pink" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-neon-pink">
              GET IN TOUCH
            </h2>
            <MessageSquare className="w-6 h-6 text-neon-pink" />
          </div>
          <p className="font-terminal text-xl text-lavender">
            Apply to join or send us an inquiry.
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex bg-panel-purple rounded-lg p-1 border-2 border-dark-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 sm:px-8 py-3 rounded-md font-pixel text-[9px] sm:text-[10px] transition-all ${
                  activeTab === tab.id
                    ? 'bg-neon-pink text-dark-border'
                    : 'text-lavender hover:text-foreground hover:bg-deep-purple/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <AnimatePresence mode="wait">
              {/* Apply Form */}
              {activeTab === 'apply' && (
                <motion.form
                  key="apply"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleApply}
                  className="arcade-panel rounded-lg p-6 sm:p-8 space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <User className="inline-block w-4 h-4 mr-1" />
                        FULL NAME
                      </label>
                      <input
                        type="text"
                        value={applyData.fullName}
                        onChange={(e) => setApplyData({ ...applyData, fullName: e.target.value })}
                        required
                        placeholder="Enter your full name..."
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <Mail className="inline-block w-4 h-4 mr-1" />
                        EMAIL
                      </label>
                      <input
                        type="email"
                        value={applyData.email}
                        onChange={(e) => setApplyData({ ...applyData, email: e.target.value })}
                        required
                        placeholder="your@email.com"
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <Phone className="inline-block w-4 h-4 mr-1" />
                        PHONE NUMBER
                      </label>
                      <input
                        type="tel"
                        value={applyData.phone}
                        onChange={(e) => setApplyData({ ...applyData, phone: e.target.value })}
                        required
                        placeholder="+63 9XX XXX XXXX"
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <MapPin className="inline-block w-4 h-4 mr-1" />
                        LOCATION
                      </label>
                      <input
                        type="text"
                        value={applyData.location}
                        onChange={(e) => setApplyData({ ...applyData, location: e.target.value })}
                        required
                        placeholder="City, Province"
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-pixel text-[10px] text-lavender mb-2">
                      <Briefcase className="inline-block w-4 h-4 mr-1" />
                      OCCUPATION / STATUS
                    </label>
                    <RetroSelect
                      name="occupation"
                      value={applyData.occupation}
                      onChange={(e) => setApplyData({ ...applyData, occupation: e.target.value })}
                      required
                      placeholder="Select your status..."
                      options={occupationOptions}
                    />
                  </div>

                  <div>
                    <label className="block font-pixel text-[10px] text-lavender mb-3">
                      <Gamepad2 className="inline-block w-4 h-4 mr-1" />
                      PREFERRED SIDE QUESTS
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {sideQuests.map((quest) => (
                        <motion.label
                          key={quest.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-all border-2 ${
                            applyData.selectedQuests.includes(quest.id)
                              ? `bg-${quest.color}/20 border-${quest.color}`
                              : 'bg-deep-purple/50 border-panel-purple hover:border-lavender/50'
                          }`}
                        >
                          <div className="relative flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={applyData.selectedQuests.includes(quest.id)}
                              onChange={() => handleQuestToggle(quest.id)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              applyData.selectedQuests.includes(quest.id)
                                ? `bg-${quest.color} border-${quest.color}`
                                : 'border-lavender/50 bg-transparent'
                            }`}>
                              {applyData.selectedQuests.includes(quest.id) && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 500 }}
                                >
                                  <CheckCircle className="w-3 h-3 text-dark-border" />
                                </motion.div>
                              )}
                            </div>
                          </div>
                          <quest.icon className={`w-4 h-4 ${applyData.selectedQuests.includes(quest.id) ? `text-${quest.color}` : 'text-lavender'}`} />
                          <span className={`font-terminal text-sm ${applyData.selectedQuests.includes(quest.id) ? 'text-foreground' : 'text-lavender'}`}>
                            {quest.label}
                          </span>
                        </motion.label>
                      ))}
                    </div>
                    <p className="font-terminal text-xs text-muted mt-2">Select all that interest you</p>
                  </div>

                  <div>
                    <label className="block font-pixel text-[10px] text-lavender mb-2">
                      <FileText className="inline-block w-4 h-4 mr-1" />
                      WHY DO YOU WANT TO JOIN?
                    </label>
                    <textarea
                      value={applyData.reason}
                      onChange={(e) => setApplyData({ ...applyData, reason: e.target.value })}
                      required
                      rows={4}
                      placeholder="Tell us why you're interested in joining Singularity Labs..."
                      className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-arcade font-pixel text-xs py-4 bg-neon-pink text-dark-border disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> SUBMITTING...</>
                    ) : (
                      <><UserPlus className="w-5 h-5" /> APPLY NOW</>
                    )}
                  </button>
                </motion.form>
              )}

              {/* Inquiry Form */}
              {activeTab === 'inquiry' && (
                <motion.form
                  key="inquiry"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleInquiry}
                  className="arcade-panel rounded-lg p-6 sm:p-8 space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <User className="inline-block w-4 h-4 mr-1" />
                        NAME
                      </label>
                      <input
                        type="text"
                        value={inquiryData.name}
                        onChange={(e) => setInquiryData({ ...inquiryData, name: e.target.value })}
                        required
                        placeholder="Enter your name..."
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                    <div>
                      <label className="block font-pixel text-[10px] text-lavender mb-2">
                        <Mail className="inline-block w-4 h-4 mr-1" />
                        EMAIL
                      </label>
                      <input
                        type="email"
                        value={inquiryData.email}
                        onChange={(e) => setInquiryData({ ...inquiryData, email: e.target.value })}
                        required
                        placeholder="your@email.com"
                        className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-pixel text-[10px] text-lavender mb-2">
                      <HelpCircle className="inline-block w-4 h-4 mr-1" />
                      SUBJECT
                    </label>
                    <RetroSelect
                      name="subject"
                      value={inquiryData.subject}
                      onChange={(e) => setInquiryData({ ...inquiryData, subject: e.target.value })}
                      required
                      placeholder="Select a subject..."
                      options={subjectOptions}
                    />
                  </div>

                  <div>
                    <label className="block font-pixel text-[10px] text-lavender mb-2">
                      <MessageSquare className="inline-block w-4 h-4 mr-1" />
                      MESSAGE
                    </label>
                    <textarea
                      value={inquiryData.message}
                      onChange={(e) => setInquiryData({ ...inquiryData, message: e.target.value })}
                      required
                      rows={6}
                      placeholder="How can we help you?"
                      className="w-full input-arcade px-4 py-3 rounded text-foreground placeholder:text-muted resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-arcade font-pixel text-xs py-4 bg-neon-pink text-dark-border disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> SENDING...</>
                    ) : (
                      <><Send className="w-5 h-5" /> SEND INQUIRY</>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="arcade-panel rounded-lg p-6 sticky top-24">
              <AnimatePresence mode="wait">
                {activeTab === 'apply' && (
                  <motion.div
                    key="apply-sidebar"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h4 className="font-pixel text-xs text-trophy-gold mb-4 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      WHY JOIN SINGULARITY LABS?
                    </h4>

                    <div className="space-y-3">
                      {[
                        'Earn real cash for your data contributions',
                        'Flexible schedule - work on your own time',
                        'Compete on leaderboards for bonus rewards',
                        'Be part of the AI/robotics revolution',
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 bg-deep-purple/50 rounded border border-panel-purple"
                        >
                          <CheckCircle className="w-5 h-5 text-success-green flex-shrink-0 mt-0.5" />
                          <span className="font-terminal text-base text-lavender">{item}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {activeTab === 'inquiry' && (
                  <motion.div
                    key="inquiry-sidebar"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h4 className="font-pixel text-xs text-trophy-gold mb-4 flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" />
                      CONTACT INFO
                    </h4>

                    <div className="space-y-4">
                      <div className="p-4 bg-deep-purple/50 rounded border border-panel-purple">
                        <p className="font-pixel text-[10px] text-lavender mb-1">EMAIL</p>
                        <p className="font-terminal text-base text-foreground">support@hdcc.ph</p>
                      </div>
                      <div className="p-4 bg-deep-purple/50 rounded border border-panel-purple">
                        <p className="font-pixel text-[10px] text-lavender mb-1">RESPONSE TIME</p>
                        <p className="font-terminal text-base text-foreground">1-3 business days</p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-neon-pink/10 rounded border border-neon-pink/30">
                      <p className="font-terminal text-sm text-lavender">
                        <MessageSquare className="inline-block w-4 h-4 mr-1 text-neon-pink" />
                        For urgent matters, include "URGENT" in your subject.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SubmitSection;
